'use client';

import { type FC, useEffect, useContext, useState } from 'react';
import { Text, Flex, Dialog } from '@/_components';
import { EditUserInfo } from './_components/EditUserInfo';
import { ClientsAddDialogProps } from './ClientsAddDialog.types';
import { ClientWithoutEmailDialog } from './_components/ClientWithoutEmailDialog';
import {
  addSpouse,
  CreateClientAttrs,
  createClient,
} from '@/_api/admin-clients';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { CompanyContext } from '../../CompanyProvider';

export const ClientsAddDialog: FC<ClientsAddDialogProps> = ({
  onClose,
  isOpen,
}) => {
  const { dropLinkUpload, setDropLinkUpload } = useContext(CompanyContext);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [localUser, setLocalUser] = useState<CreateClientAttrs | undefined>(
    undefined,
  );
  const [localSpouse, setLocalSpouse] = useState<any | undefined>(undefined);
  const [isConfirmMissingEmail, setConfirmMissingEmail] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      setLocalUser(undefined);
      setLoading(false);
      setLocalSpouse(undefined);
    }
  }, [isOpen]);

  const { addOrUpdateUser } = useContext(CompanyContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const onCloseConfirmEmail = () => setConfirmMissingEmail(false);

  const onConfirmUserWithoutEmail = () => {
    if (!localUser) return;

    onSave(localUser, localSpouse, true);
    setConfirmMissingEmail(false);
  };

  const onShowConfirm = (user: CreateClientAttrs, spouse: any) => {
    setLocalUser(user);
    setLocalSpouse(spouse);
    setConfirmMissingEmail(true);
  };

  const onSave = async (
    user: CreateClientAttrs,
    spouse: any,
    skipEmailCheck?: boolean,
  ) => {
    if (!user?.email && !skipEmailCheck) {
      return onShowConfirm(user, spouse);
    }

    try {
      setLoading(true);

      const {
        error,
        user: newUser,
        company_id,
      } = await createClient({
        ...user,
        dropLinkUploadId: dropLinkUpload?.id,
        token,
      });

      if (error && error.includes('is already taken')) {
        return addToast(
          'error',
          'Email is already taken. Learn how to resolve this issue <a href="https://whimsical-split-479.notion.site/Handling-a-Client-Formerly-at-Another-Firm-3aa64306c9444d80b8cf815aadeaac96" target="_blank">here</a>.',
        );
      }

      if (newUser?.id) {
        addOrUpdateUser(newUser);
        setDropLinkUpload(null);
      }

      if (spouse) {
        await addSpouse({
          companyId: company_id,
          params: spouse,
          token,
        });
      }

      const toastMessage: string = spouse
        ? 'Successfully created a new user and spouse'
        : 'Successfully created a new user';

      addToast('success', toastMessage);
      onClose();
    } catch (error) {
      console.error({ error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog isOpen={isOpen} title="Create User and Entity" onClose={onClose}>
        <Dialog.Content>
          <Flex isColumn={true} gap={12}>
            <Text type="small">
              If you&apos;d like to create a user for an existing entity, please
              first navigate to that entity and click <b>Add User</b> then{' '}
              <b>New User</b>.
            </Text>
            <EditUserInfo
              isOpen={isOpen}
              onSave={onSave}
              onClose={onClose}
              isLoading={isLoading}
            />
          </Flex>
        </Dialog.Content>
      </Dialog>
      <ClientWithoutEmailDialog
        onClose={onCloseConfirmEmail}
        onConfirm={onConfirmUserWithoutEmail}
        isOpen={isConfirmMissingEmail}
      />
    </>
  );
};
